import type React from 'react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import classnames from 'classnames'
import dayjs, { type Dayjs } from 'dayjs'
import '@szhsin/react-menu/dist/core.css'

import { useSaveNote } from '../../hooks/useSaveNote'
import useNote, { type useNoteConfig } from '../../hooks/useNote'
import { useTaskMove } from '../../hooks/useTaskMove'
import {
  extractTasksFromDragEvent,
  isDragEventWithTasks,
} from '../../utils/taskDragUtils'
import { NoteType, dateToKey } from '../../utils/note'
import {
  type SelectedDate,
  isSameDay,
  isSameWeek,
  selectedDateToDay,
  selectedDateToKey,
  weekToDate,
} from '../../contexts/SelectedDate'
import { useUserState } from '../../providers/UserProvider'
import useBulletAndTaskItemCharacters from '../../hooks/useBulletAndTaskItemCharacters'
import { type WindowWithEditor } from '../../utils/WindowWithEditor'
import { eachDayOfMonth, eachWeekOfMonth } from './eachDayOfX'
import { WeekDays } from './WeekDays'
import { CalendarMenu } from './CalendarMenu'
import {
  usePrivateCalendarNotesByDate,
  useTeamCalendarNotesByDate,
} from '../../hooks/useNotesFactory'
import { FromNote, useReferences } from '../../providers/ReferencesContext'
import {
  TASK_BLOCK_TYPES,
  isBlockType,
  isBlockChecked,
  isBlockCancelled,
  isBlockScheduled,
} from '../../components/shared/BlockRenderer'

function monthToDate({ month, year }: { month: number; year: number }): Dayjs {
  return dayjs().year(year).month(month)
}

function isSameMonth(
  { month, year }: { month: number; year: number },
  date: Dayjs
): boolean {
  return date.month() === month && date.year() === year
}

function onSaveSuccess() {
  // remove dragged item

  const editor = (window as WindowWithEditor).editor
  editor?._tiptapEditor.chain().focus().deleteSelection().run()
}

export type CalendarProps = {
  selectedDate: SelectedDate
  onToday: () => void
  onChangeWeek: (_week: { week: number; year: number }) => void
  onChangeDay: (_day: Dayjs) => void
  timelineDays: number
  responsive?: boolean // When true, adapts to container width instead of fixed width
  // onSetTimelineDays: (_days: number) => void;
}
export function Calendar({
  selectedDate,
  onToday,
  onChangeWeek,
  onChangeDay,
  timelineDays,
  responsive = false,
}: // onSetTimelineDays,
CalendarProps) {
  const user = useUserState()
  const selectedDay = selectedDateToDay(selectedDate)
  const [currentMonth, setCurrentMonth] = useState({
    month: selectedDay.month(),
    year: selectedDay.year(),
  })
  const days = useMemo(
    () => eachDayOfMonth(currentMonth.month, currentMonth.year),
    [currentMonth]
  )
  const weeks = useMemo(
    () => eachWeekOfMonth(currentMonth.month, currentMonth.year),
    [currentMonth]
  )

  const { data: privateCalendarNotesByDate } = usePrivateCalendarNotesByDate()
  const { data: teamCalendarNotesByDate } = useTeamCalendarNotesByDate()

  const references = useReferences()

  const { taskItemCharacters } = useBulletAndTaskItemCharacters()
  const { moveTaskToDate } = useTaskMove()

  // Query references and notes if they have open tasks for the days in the list
  const hasTasks = useMemo(() => {
    return days.map((day) => {
      const dayKey = dateToKey(day, 'day')

      const incomingReferences =
        references.get(dayKey) ?? new Map<string, FromNote>()
      for (const fromNote of incomingReferences.values()) {
        for (const line of fromNote.lines) {
          if (
            isBlockType(line.block, TASK_BLOCK_TYPES) &&
            !isBlockChecked(line.block) &&
            !isBlockCancelled(line.block) &&
            !isBlockScheduled(line.block)
          ) {
            return true
          }
        }
      }

      const char = taskItemCharacters.join('|')
      const dayNotes = [
        ...(privateCalendarNotesByDate?.get(dayKey) ?? []),
        ...(teamCalendarNotesByDate?.get(dayKey) ?? []),
      ]

      return dayNotes.some(
        (note) =>
          note.content &&
          new RegExp(
            `^(\\s*[${char}] (?!.*\\[x\\]|\\[-\\]|\\[>\\]))`,
            'gm'
          ).test(note.content)
      )
    })
  }, [
    days,
    privateCalendarNotesByDate,
    references,
    taskItemCharacters,
    teamCalendarNotesByDate,
  ])

  function handlePreviousMonth() {
    const newDate = dayjs()
      .year(currentMonth.year)
      .month(currentMonth.month)
      .subtract(1, 'month')
    setCurrentMonth(() => ({ month: newDate.month(), year: newDate.year() }))
  }

  function handleNextMonth() {
    const newDate = dayjs()
      .year(currentMonth.year)
      .month(currentMonth.month)
      .add(1, 'month')
    setCurrentMonth(() => ({ month: newDate.month(), year: newDate.year() }))
  }

  // jump to month if selectedDate changed
  useEffect(() => {
    if (selectedDay && !isSameMonth(currentMonth, selectedDay)) {
      setCurrentMonth(() => ({
        month: selectedDay.month(),
        year: selectedDay.year(),
      }))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDay]) // Don't add the dependency on currentMonth, otherwise it will not switch to the next month clicking the arrow buttons

  // #region <drop>
  const [dropDate, setDropDate] = useState<SelectedDate | undefined>(undefined)
  const noteKey = useCallback((): useNoteConfig | undefined => {
    if (!dropDate) {
      return
    }

    return {
      noteType: selectedDate.teamspace
        ? NoteType.TEAM_SPACE_CALENDAR_NOTE
        : NoteType.CALENDAR_NOTE,
      recordName: undefined,
      filename: selectedDateToKey(dropDate),
      // eslint-disable-next-line unicorn/no-null -- supabase needs null
      parent: selectedDate.teamspace ?? null,
    }
  }, [dropDate, selectedDate.teamspace])

  const { data: dropNote } = useNote(noteKey())
  const [dropText, setDropText] = useState<string | undefined>(undefined)
  const saveDropNote = useSaveNote(onSaveSuccess)

  // prepend task on drop
  useEffect(() => {
    if (dropNote && dropText && dropNote.recordName) {
      saveDropNote.mutate({
        filename: dropNote.filename ?? '',
        recordName: dropNote.recordName ?? '',
        content: `${dropText}\n${dropNote.content ?? ''}`,
        attachments:
          JSON.parse(
            dropNote.attachments && dropNote.attachments.length > 0
              ? dropNote.attachments
              : '[]'
          ) ?? [],
        noteType: dropNote.noteType,
        parent: dropNote.parent,
        modificationDate: new Date(),
      })
      setDropDate(undefined)
      setDropText(undefined)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dropNote, dropText])

  // Robust task drop handler using the new transactional approach
  function handleTaskDrop(
    e: React.DragEvent<HTMLButtonElement>,
    targetDate?: SelectedDate
  ) {
    // Prevent default drag behavior
    e.preventDefault()
    e.stopPropagation()

    // Check if this is a task drag event
    if (!isDragEventWithTasks(e)) {
      return
    }

    // Ensure we have a valid drop target
    const effectiveDropDate = targetDate || dropDate
    if (!effectiveDropDate) {
      return
    }

    try {
      // Determine if this is a CloudKit note for attachment handling
      const isCloudKitSignedIn = Boolean(user?.cloudKitUserId)
      const isCloudKitNote = !selectedDate.teamspace && isCloudKitSignedIn

      // Extract tasks from the drag event
      const extractionResult = extractTasksFromDragEvent(e, isCloudKitNote)

      if (!extractionResult.success || extractionResult.tasks.length === 0) {
        console.warn(
          'Failed to extract tasks from drag event:',
          extractionResult.errors
        )
        return
      }

      // Determine target note type
      const targetNoteType = selectedDate.teamspace
        ? NoteType.TEAM_SPACE_CALENDAR_NOTE
        : NoteType.CALENDAR_NOTE

      // Execute the robust task move

      void moveTaskToDate(
        extractionResult.tasks,
        effectiveDropDate,
        targetNoteType,
        selectedDate.teamspace || undefined
      )

      // Delete the dragged selection from the editor
      onSaveSuccess()
    } catch (error) {
      console.error('Error in handleTaskDrop:', error)
    } finally {
      // Clean up drop state
      setDropDate(undefined)
      setDropText(undefined)
    }
  }
  // #endregion

  return (
    <div
      id='calendar-view'
      style={responsive ? {} : { width: 250 + timelineDays * 50 }}
      className={responsive ? 'w-full' : ''}
    >
      <div className='pt-0 text-center lg:col-start-8 lg:col-end-13 lg:row-start-1 xl:col-start-9'>
        <div className='flex items-center justify-between space-x-2 pl-2 pt-0 text-gray-900'>
          <div className='flex flex-auto'>
            <button
              type='button'
              data-tooltip-id='my-tooltip'
              data-tooltip-content='Open today (⌃T)'
              className='flex flex-none text-left text-lg text-black hover:bg-gray-500/25 dark:text-white'
              onClick={onToday}
            >
              {monthToDate(currentMonth).format('MMMM YYYY')}
            </button>
          </div>

          <CalendarMenu />
          {/* <SettingsMenu timelineDays={timelineDays} onSetTimelineDays={onSetTimelineDays} /> */}
          <button
            type='button'
            className='flex flex-none items-center justify-center p-1.5 hover:bg-zinc-100 dark:hover:bg-zinc-700'
            onClick={handlePreviousMonth}
          >
            <span className='sr-only'>Previous month</span>
            <i
              className='fa-solid fa-chevron-left h-5 w-5 text-sm'
              aria-hidden='true'
            />
          </button>
          <button
            type='button'
            className='flex flex-none items-center justify-center p-1.5 hover:bg-zinc-100 dark:hover:bg-zinc-700'
            onClick={handleNextMonth}
          >
            <span className='sr-only'>Next month</span>
            <i
              className='fa-solid fa-chevron-right h-5 w-5 text-sm'
              aria-hidden='true'
            />
          </button>
        </div>

        <div className='ml-2 mt-2 flex'>
          <div className='calendar-weeks flex flex-col'>
            <div className='mb-1 text-xs text-orange-500'>CW</div>
            {weeks.map((week) => (
              <button
                key={`calendar-week-number-${week.week}`}
                type='button'
                onClick={() => {
                  onChangeWeek(week)
                }}
                onDrop={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  if (!isSameWeek(selectedDate, week)) {
                    // remove highlights
                    e.currentTarget.classList.remove(
                      'bg-orange-200/40',
                      'border-orange-400'
                    )
                    e.currentTarget.classList.add('border-transparent')
                    // fetch note
                    const weekDropDate: SelectedDate = {
                      ...week,
                      activePeriod: 'week' as const,
                      date: weekToDate(week),
                    }
                    setDropDate(weekDropDate)
                    handleTaskDrop(e, weekDropDate)
                  }
                }}
                onDragEnd={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  if (!isSameWeek(selectedDate, week)) {
                    // remove highlights
                    e.currentTarget.classList.remove(
                      'bg-orange-200/40',
                      'border-orange-400'
                    )
                    e.currentTarget.classList.add('border-transparent')
                  }
                }}
                onDragOver={(e) => {
                  e.preventDefault()
                  e.stopPropagation()

                  if (!isSameWeek(selectedDate, week)) {
                    // add highlights
                    e.currentTarget.classList.add(
                      'bg-orange-200/40',
                      'border-orange-400'
                    )
                    e.currentTarget.classList.remove('border-transparent')
                  }
                }}
                onDragLeave={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  if (!isSameWeek(selectedDate, week)) {
                    // remove highlights
                    e.currentTarget.classList.remove(
                      'bg-orange-200/40',
                      'border-orange-400'
                    )
                    e.currentTarget.classList.add('border-transparent')
                  }
                }}
                onDragEnter={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                }}
                className={classnames(
                  'm-[0.053rem] rounded-none border-transparent bg-transparent pb-[0.48em] pl-[0.49em] pr-[0.49em] pt-[0.32em] text-xs leading-tight text-orange-500 hover:bg-zinc-100 focus:z-10 dark:hover:bg-zinc-700',
                  {
                    '!bg-blue-300/30 font-semibold text-gray-600':
                      selectedDate.activePeriod === 'week' &&
                      isSameWeek(selectedDate, week),
                  }
                )}
              >
                <div className='flex flex-col items-center space-y-0'>
                  <span className='mb-2'>{week.week}</span>
                </div>
              </button>
            ))}
          </div>

          <div className='calendar-days flex-auto'>
            <WeekDays />

            <div className='isolate mt-1 grid w-full grid-cols-7 bg-transparent text-sm'>
              {days.map((day, dayIdx) => (
                <button
                  id={`${dayIdx}: ${isSameDay(selectedDate, day)}`}
                  key={`calendar-day-view-${day}-${dayIdx}`}
                  type='button'
                  onClick={() => {
                    onChangeDay(day)
                  }}
                  className={classnames(
                    'border-inset m-[0.05rem] rounded-none border-2 border-transparent leading-tight hover:bg-zinc-100 focus:z-10 dark:hover:bg-zinc-700',
                    {
                      'font-semibold':
                        selectedDate.activePeriod === 'day' &&
                        (isSameDay(selectedDate, day) ||
                          day.isSame(dayjs(), 'day')),
                      'text-gray-900 dark:text-gray-50':
                        !isSameDay(selectedDate, day) &&
                        isSameMonth(currentMonth, day) &&
                        !day.isSame(dayjs(), 'day'),
                      'text-gray-400':
                        !isSameDay(selectedDate, day) &&
                        !isSameMonth(currentMonth, day) &&
                        !day.isSame(dayjs(), 'day'),
                      'text-blue-400': day.isSame(dayjs(), 'day'),
                      'bg-red-300/25':
                        !isSameDay(selectedDate, day) &&
                        hasTasks[dayIdx] &&
                        day.isBefore(dayjs().startOf('day')),
                      'bg-blue-300/30':
                        selectedDate.activePeriod === 'day' &&
                        isSameDay(selectedDate, day),
                    },
                    dayIdx === 0 &&
                      day.weekday() > 0 &&
                      `col-start-${day.weekday()}`
                  )}
                  onDrop={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    if (!isSameDay(selectedDate, day)) {
                      // remove highlights
                      e.currentTarget.classList.remove(
                        'bg-orange-200/40',
                        'border-orange-400'
                      )
                      e.currentTarget.classList.add('border-transparent')
                      // fetch note
                      const dayDropDate: SelectedDate = {
                        activePeriod: 'day' as const,
                        date: day,
                        week: day.week(),
                        year: day.year(),
                      }
                      setDropDate(dayDropDate)
                      handleTaskDrop(e, dayDropDate)
                    }
                  }}
                  onDragEnd={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    if (!isSameDay(selectedDate, day)) {
                      // remove highlights
                      e.currentTarget.classList.remove(
                        'bg-orange-200/40',
                        'border-orange-400'
                      )
                      e.currentTarget.classList.add('border-transparent')
                    }
                  }}
                  onDragOver={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    if (!isSameDay(selectedDate, day)) {
                      // add highlights
                      e.currentTarget.classList.add(
                        'bg-orange-200/40',
                        'border-orange-400'
                      )
                      e.currentTarget.classList.remove('border-transparent')
                    }
                  }}
                  onDragLeave={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    if (!isSameDay(selectedDate, day)) {
                      // remove highlights
                      e.currentTarget.classList.remove(
                        'bg-orange-200/40',
                        'border-orange-400'
                      )
                      e.currentTarget.classList.add('border-transparent')
                    }
                  }}
                  onDragEnter={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                  }}
                >
                  <div className='flex flex-col items-center space-y-0'>
                    <time
                      dateTime={day.format('yyyy-MM-DD')}
                      className={classnames('text-center')}
                    >
                      {day.date()}
                    </time>
                    <svg
                      className={`stroke-current text-orange-500 ${
                        hasTasks[dayIdx] ? 'opacity-100' : 'opacity-0'
                      }`}
                      xmlns='http://www.w3.org/2000/svg'
                      viewBox='0 0 8 8'
                      fill='none'
                      strokeWidth='1'
                      width='8'
                      height='8'
                    >
                      <circle cx='4' cy='4' r='2.3' />
                    </svg>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
